import type { SubComponent } from './utils/SubComponent';

import { TooltipProvider } from '@usealloy/component-library';
import React, { useRef } from 'react';

import { useComposedRefs } from './hooks/useComposedRefs';
import * as SidebarContent from './SidebarContent';
import { useSidebarCtx } from './SidebarContext';
import * as SidebarExpandToggle from './SidebarExpandToggle';
import * as SidebarGroup from './SidebarGroup';
import * as SidebarItem from './SidebarItem';
import * as SidebarMenu from './SidebarMenu';
import { SidebarProvider } from './SidebarProvider';
import type { ContentWrapperProps } from './SidebarWrapper';
import { ContentWrapper } from './SidebarWrapper';

// ============================================================
//     TYPES
// ============================================================
interface ProviderProps extends React.ComponentProps<typeof SidebarProvider> {}

interface SidebarProps extends ContentWrapperProps, Omit<ProviderProps, 'children'> {}

interface SidebarCompound
  extends
  React.ForwardRefExoticComponent<SidebarProps & React.RefAttributes<HTMLDivElement>>,
  SubComponent<typeof SidebarContent>,
  SubComponent<typeof SidebarExpandToggle>,
  SubComponent<typeof SidebarGroup>,
  SubComponent<typeof SidebarMenu>,
  SubComponent<typeof SidebarItem> {
  /**
   * Type safe context hook for accessing the sidebar state from another component.
   *
   * Useful for conditionally rendering component based on the sidebars current state.
   *
   * @throws If used outside of a `<Sidebar>` component
   *
   * @example
   * ```tsx
   * const { isExpanded, toggleSidebar } = Sidebar.useSidebarCtx();
   * ```
   */
  useSidebarCtx: typeof useSidebarCtx;
}

// ============================================================
//     COMPONENT
// ============================================================
/**
 * A collapsible navigation sidebar.
 *
 * Ported from the Frontend-Platform `Sidebar` compound component, adapted for Daffy
 * (React 18, styled-components, component-library CSS variables).
 *
 * Renders as a sticky element on desktop, or fixed to the viewport edge with the
 * `primary` variant. Supports controlled/uncontrolled expand state, a global keyboard
 * shortcut, and custom widths.
 *
 * #### When to use:
 * - Primary app navigation
 * - Settings pages with nested navigation sections
 * - Any layout requiring persistent, collapsible navigation
 *
 * #### Important Props:
 * - `isCollapsible` - enables collapse/expand behavior
 * - `isMobileEnabled` - enables responsive mobile drawer mode
 * - `keyboardShortcut` - global keyboard shortcut to toggle sidebar
 * - `variant` - Controls how the sidebar renders for desktop viewports
 *
 * #### SubComponents:
 * - `Sidebar.Header` - fixed top content section
 * - `Sidebar.Body` - scrollable main content area
 * - `Sidebar.Footer` - fixed bottom content section
 * - `Sidebar.Group` - logical grouping container for menu sections
 * - `Sidebar.GroupLabel` - heading/label for a group
 * - `Sidebar.GroupAction` - action button within a group header
 * - `Sidebar.GroupContent` - wraps a group's collapsible contents (used with `Sidebar.Group collapsible`)
 * - `Sidebar.Menu` - list wrapper for menu items (supports nested submenus)
 * - `Sidebar.MenuItem` - individual menu item container
 * - `Sidebar.MenuButton` - clickable navigation button
 * - `Sidebar.MenuAction` - secondary action button on a menu item
 * - `Sidebar.MenuBadge` - notification badge indicator
 *
 * @example
 * ```tsx
 * <Sidebar isCollapsible>
 *   <Sidebar.Header>
 *     <Logo />
 *   </Sidebar.Header>
 *   <Sidebar.Body>
 *     <Sidebar.Group>
 *       <Sidebar.GroupLabel>Navigation</Sidebar.GroupLabel>
 *       <Sidebar.Menu>
 *         <Sidebar.MenuItem>
 *           <Sidebar.MenuButton isActive tooltip="Dashboard">
 *             <Icon icon={IconName.Home} size="small" />
 *             <span>Dashboard</span>
 *           </Sidebar.MenuButton>
 *         </Sidebar.MenuItem>
 *         <Sidebar.MenuItem>
 *           <Sidebar.MenuButton tooltip="Settings">
 *             <Icon icon={IconName.Settings} size="small" />
 *             <span>Settings</span>
 *           </Sidebar.MenuButton>
 *           <Sidebar.MenuBadge value={3} />
 *         </Sidebar.MenuItem>
 *       </Sidebar.Menu>
 *     </Sidebar.Group>
 *   </Sidebar.Body>
 *   <Sidebar.Footer>
 *     <UserProfile />
 *   </Sidebar.Footer>
 * </Sidebar>
 * ```
 */
const SidebarRoot = React.forwardRef<HTMLDivElement, SidebarProps>((props, forwardedRef) => {
  const {
    children,
    defaultExpanded = true,
    expanded: expandedProp,
    isCollapsible = false,
    isMobileEnabled = false,
    isOpenMobile: isOpenMobileProp,
    keyboardShortcut,
    onExpandedChange: onExpandedChangeProp,
    onOpenMobileChange: onOpenMobileChangeProp,
    shouldShowMobile: shouldShowMobileProp,
    ...restProps
  } = props;

  const sidebarRef = useRef<HTMLDivElement>(null);
  const composedSidebarRef = useComposedRefs(sidebarRef, forwardedRef);

  return (
    <TooltipProvider delayDuration={250} disableHoverableContent>
      <SidebarProvider
        defaultExpanded={defaultExpanded}
        expanded={expandedProp}
        isCollapsible={isCollapsible}
        isMobileEnabled={isMobileEnabled}
        isOpenMobile={isOpenMobileProp}
        keyboardShortcut={keyboardShortcut}
        onExpandedChange={onExpandedChangeProp}
        onOpenMobileChange={onOpenMobileChangeProp}
        shouldShowMobile={shouldShowMobileProp}
      >
        <ContentWrapper ref={composedSidebarRef} {...restProps}>
          {children}
        </ContentWrapper>
      </SidebarProvider>
    </TooltipProvider>
  );
});

SidebarRoot.displayName = 'Sidebar' as const;

export const Sidebar = SidebarRoot as SidebarCompound;

// =====  SUBCOMPONENTS  =====

// CONTEXT
Sidebar.useSidebarCtx = useSidebarCtx;

// CONTENT WRAPPERS
Sidebar.Header = SidebarContent.Header;
Sidebar.Footer = SidebarContent.Footer;
Sidebar.Body = SidebarContent.Body;

// ACTIONS
Sidebar.ExpandToggle = SidebarExpandToggle.ExpandToggle;

// GROUPS
Sidebar.Group = SidebarGroup.Group;
Sidebar.GroupLabel = SidebarGroup.GroupLabel;
Sidebar.GroupAction = SidebarGroup.GroupAction;
Sidebar.GroupContent = SidebarGroup.GroupContent;

// MENU
Sidebar.Menu = SidebarMenu.Menu;
Sidebar.MenuItem = SidebarItem.MenuItem;
Sidebar.MenuButton = SidebarItem.MenuButton;
Sidebar.MenuAction = SidebarItem.MenuAction;
Sidebar.MenuBadge = SidebarItem.MenuBadge;
